<script setup>
/* eslint-disable vue/no-mutating-props */
import { ref } from 'vue'
import HelpText from './HelpText.vue'
import Multiselect from '@vueform/multiselect'
import { isEmptyValue } from '@/utils/trafficSourceCostsUtils'

const multiselect$ = ref(null)
const props = defineProps({
  trafficSourceCost: {
    type: Object,
    required: true,
  },
  trafficSources: {
    type: Array,
    required: true,
  },
  currency: {
    type: String,
    default: '',
  },
  useInvoice: {
    type: Boolean,
    default: true,
  },
})
const emit = defineEmits(['update', 'remove'])

const handleInputChange = (value, pairedField) => {
  if (!isEmptyValue(value) && isEmptyValue(props.trafficSourceCost[pairedField])) {
    emit('update', { field: pairedField, value: 0 })
  }
}
const handleSelectAll = () => {
  multiselect$.value.selectAll()
  multiselect$.value.close()
}
const handleSelect = (data) => emit('update', { field: 'sourceIndexes', value: data })
</script>
<template>
  <div class="trafficSourceCost__container">
    <div class="trafficSourceCost__content">
      <div class="title-container">
        <label>Traffic Source</label>
        <HelpText placement="bottom-start">
          <div class="hint-container">
            <div>
              <p class="hint-title">Instagram</p>
              <p class="hint-description">Traffic coming from native Instagram app.</p>
            </div>
            <div>
              <p class="hint-title">TikTok</p>
              <p class="hint-description">Traffic coming from native TikTok app.</p>
            </div>
            <div>
              <p class="hint-title">Snapchat</p>
              <p class="hint-description">Traffic coming from native Snapchat app.</p>
            </div>
            <div>
              <p class="hint-title">Other</p>
              <p class="hint-description">
                Traffic coming from any other source, e.g. websites (blogs, YouTube), messaging apps (WhatsApp, Telegram) and manual link
                sharing.
              </p>
            </div>
          </div>
        </HelpText>
      </div>
      <Multiselect
        ref="multiselect$"
        v-model="trafficSourceCost.sourceIndexes"
        :options="trafficSources"
        mode="tags"
        label="name"
        value-prop="source"
        track-by="source"
        placeholder="Select source"
        class="multiselect"
        :classes="{ optionPointed: false, clear: false, clearIcon: false }"
        @change="handleSelect"
      >
        <template #tag="{ option, handleTagRemove }">
          <div class="multiselect-tag">
            {{ option.name }}
            <span class="multiselect-tag-remove" @mousedown.prevent="handleTagRemove(option, $event)">
              <span class="multiselect-tag-remove-icon"></span>
            </span>
          </div>
        </template>
        <template v-if="trafficSourceCost.sourceIndexes.length < trafficSources.length" #afterlist>
          <div class="multiselect-option multiselect-select-all" @click.prevent="handleSelectAll">Select all</div>
        </template>
      </Multiselect>
      <div class="inputs-container">
        <div class="input-wrapper">
          <label class="label">CPC</label>
          <el-input
            v-model="trafficSourceCost.rateCpc"
            type="number"
            pattern="[0-9]+([\.,][0-9]+)?"
            class="traffic-source-value"
            placeholder="0.75"
            maxlength="10"
            @input="(value) => handleInputChange(value, 'rateCpa')"
          >
            <template #suffix>
              <span class="suffix">{{ currency }} / Click</span>
            </template>
          </el-input>
        </div>
        <div class="input-wrapper">
          <label class="label">CPA</label>
          <el-input
            v-model="trafficSourceCost.rateCpa"
            type="number"
            pattern="[0-9]+([\.,][0-9]+)?"
            maxlength="10"
            placeholder="15%"
            class="traffic-source-value"
            @input="(value) => handleInputChange(value, 'rateCpc')"
          >
            <template #suffix>
              <span class="suffix">% / Sale</span>
            </template>
          </el-input>
        </div>
        <div v-if="useInvoice" class="input-wrapper">
          <label class="label">Invoice CPC</label>
          <el-input
            v-model="trafficSourceCost.rateInvoiceCpc"
            type="number"
            pattern="[0-9]+([\.,][0-9]+)?"
            placeholder="0.85"
            maxlength="10"
            class="traffic-source-value"
            @input="(value) => handleInputChange(value, 'rateInvoiceCpa')"
          >
            <template #suffix>
              <span class="suffix">{{ currency }} / Click</span>
            </template>
          </el-input>
        </div>
        <div v-if="useInvoice" class="input-wrapper">
          <label class="label">Invoice CPA</label>
          <el-input
            v-model="trafficSourceCost.rateInvoiceCpa"
            type="number"
            pattern="[0-9]+([\.,][0-9]+)?"
            maxlength="10"
            placeholder="15%"
            class="traffic-source-value"
            @input="(value) => handleInputChange(value, 'rateInvoiceCpc')"
          >
            <template #suffix>
              <span class="suffix">% / Sale</span>
            </template>
          </el-input>
        </div>
      </div>
    </div>
    <a @click="() => $emit('remove')">
      <trash-icon class="trash-icon" />
    </a>
  </div>
</template>

<style lang="scss" scoped>
:deep(.el-input) {
  .el-input__inner {
    border: none;
  }
}
.trafficSourceCost__container {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  border-radius: 2px;
  border: 1px solid #e2e2e2;
  background: #fdfdfd;
}

.trafficSourceCost__content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hint-container {
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hint-title {
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
}

.hint-description {
  color: #6f6f6f;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  text-align: left;
  word-break: keep-all;
}

.inputs-container {
  display: flex;
  gap: 16px;
}

.input-wrapper {
  width: 100%;

  .label {
    color: #000;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
    margin-bottom: 4px;
  }
}

.hide {
  display: none;
}

.suffix {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.multiselect {
  border-radius: 2px;
  border: 1px solid #e2e2e2;
  background: #fff;
  --ms-font-size: 16px;
  --ms-line-height: 1.5;
  --ms-py: 14px;
  --ms-px: 12px;
  --ms-bg: #ffffff;
  --ms-border-color: #e2e2e2;
  --ms-border-width: 2px;
  --ms-option-font-size: 16px;
  --ms-option-line-height: 24px;
  --ms-option-py: 8px;
  --ms-option-px: 16px;
  --ms-tag-font-size: 16px;
  --ms-tag-line-height: 24px;
  --ms-tag-font-weight: 400;
  --ms-tag-bg: rgba(216, 0, 119, 0.08);
  --ms-tag-color: #d80077;
  --ms-tag-color-disabled: #ffffff;
  --ms-tag-radius: 4px;
  --ms-tag-py: 3px;
  --ms-tag-px: 8px;
  --ms-tag-my: 4px;
  --ms-tag-mx: 8px;
  --ms-tag-remove-radius: 0px;
  --ms-tag-remove-py: 0px;
  --ms-tag-remove-px: 0px;
  --ms-tag-remove-my: 3px;
  --ms-tag-remove-mx: 3px;

  &.is-active {
    box-shadow: none;
  }

  .multiselect-tag-remove-icon {
    width: 18px;
    height: 18px;
  }

  .multiselect-tag {
    border: 1px solid #d80077;
  }

  .multiselect-select-all {
    font-weight: 600;
    border-top: 1px solid #e2e2e2;
  }
}
</style>
<style lang="scss">
.traffic-source-value {
  .el-input__inner {
    border-radius: 2px;
    border: 1px solid #e2e2e2;
    box-shadow: none;
    font-size: 16px;
    line-height: 24px;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    -moz-appearance: textfield;
  }
}
</style>
<style src="@vueform/multiselect/themes/default.css"></style>
